import * as React from 'react';
import { useCommonTracking } from 'common-tracking';
import { getEnvironment } from 'common-utils';
import { useRouter } from 'next/router';
import { getCountryCodeAllInfo } from 'preflight-core';
import { useEffect } from 'react';
import { z } from 'zod';

let debounce: NodeJS.Timeout;

type EventCategory = z.infer<typeof EventCategory>;
const EventCategory = z.enum([
  'Interstitial',
  'Processing_page',
  'Manuscript upload intent',
  'Result_page',
  'Checkout',
  'Error',
  'Sign-up Intent',
  'Sign-in Intent',
  'Data_Security',
  'SSO',
  'User_history_page',
  'Header',
  'Manuscript upload completion',
]);

type EventAction = z.infer<typeof EventAction>;
const EventAction = z.enum([
  'Filling Details',
  'Filling Details_b',
  'Upload click',
  'Upload drag and drop',
  'Upload_failed',
  'Upload_completed',
  'Processing_starts',
  'Processing_failed',
  'Processing_completed',
  'Secondary click',
  'Download_document',
  'Payment_process',
  'Top_CTA',
  'Bottom_CTA',
  'OTP',
  'Delete_ducument',
  'Logout_button',
  'Login_button',
  'Login_form',
]);

type EventLabel = z.infer<typeof EventLabel>;
const EventLabel = z.enum([
  'Check your manuscript now button',
  'Email ID',
  'Upload your manuscript file button',
  'Dropzone',
  'Start pre submission check button',
  'File upload completed',
  'File processing started',
  'File_processing_failed',
  'Continue to submission button',
  '$29 get the full document button',
  'Download_the_full_edited_document',
  'Download_sample_edited_document',
  'More_Recommendations_Label_Clicked',
  'Checkout_dialog_opens',
  'Payment failed',
  'Payment successful',
  'Payment aborted',
  'Submit Email ID',
  'Create Paperpal account clicked',
  'Marketing_consent_given',
  'Name',
  'Email',
  'CC_Number',
  'Expiry_month',
  'Payment',
  'Country',
  'Currency',
  'Terms_accepted',
  'Upload_Manuscript_Info_Button',
  'Why_Preflight_Data_Security_Details',
  'Login_button_Clicked',
  'Forgot_password_success',
  'Forgot_password_link',
  'Signup_link',
  'Get_otp_cta',
  'Forgot_password_login_cta',
  'Forgot_password_try_again_link',
  'Get started failed',
  'Sign up successful',
  'Sign_in_with_OTP',
  'Delete_ducument',
  'logout',
  'Log in',
]);

function sle<
  TEventCategory extends EventCategory,
  TEventAction extends EventAction,
  TEventLabel extends EventLabel,
>(
  category: TEventCategory,
  action: TEventAction,
  label: TEventLabel,
): [TEventCategory, TEventAction, TEventLabel] {
  return [category, action, label];
}

type StaticLabelEvent = (typeof staticLabelEvents)[number];
const staticLabelEvents = [
  sle(
    'Manuscript upload intent',
    'Upload click',
    'Upload your manuscript file button',
  ),
  sle(
    'Manuscript upload intent',
    'Upload click',
    'Check your manuscript now button',
  ),
  sle('Manuscript upload intent', 'Upload click', 'Dropzone'),
  sle('Manuscript upload intent', 'Upload drag and drop', 'Dropzone'),
  sle('Processing_page', 'Upload_completed', 'File upload completed'),
  sle('Processing_page', 'Processing_starts', 'File processing started'),
  sle('Error', 'Processing_failed', 'File_processing_failed'),
  sle(
    'Manuscript upload intent',
    'Upload click',
    'Upload your manuscript file button',
  ),
  sle(
    'Manuscript upload intent',
    'Upload click',
    'Check your manuscript now button',
  ),
  sle(
    'Manuscript upload intent',
    'Upload click',
    'Start pre submission check button',
  ),
  sle(
    'Manuscript upload intent',
    'Secondary click',
    'Continue to submission button',
  ),
  sle('Result_page', 'Download_document', '$29 get the full document button'),
  sle('Result_page', 'Download_document', 'Download_the_full_edited_document'),
  sle('Result_page', 'Download_document', 'Download_sample_edited_document'),
  sle('Checkout', 'Payment_process', 'Checkout_dialog_opens'),
  sle('Checkout', 'Payment_process', '$29 get the full document button'),
  sle('Checkout', 'Payment_process', 'Payment successful'),
  sle('Checkout', 'Filling Details', 'Name'),
  sle('Checkout', 'Filling Details', 'Email'),
  sle('Checkout', 'Filling Details', 'Payment'),
  sle('Checkout', 'Filling Details', 'Country'),
  sle('Checkout', 'Filling Details', 'Currency'),
  sle('Checkout', 'Filling Details', 'Terms_accepted'),
  sle('Error', 'Payment_process', 'Payment failed'),
  sle('Checkout', 'Payment_process', 'Payment aborted'),
  sle('Sign-up Intent', 'Filling Details', 'Email ID'),
  sle('Sign-up Intent', 'Filling Details', 'Submit Email ID'),
  sle('Sign-up Intent', 'Filling Details', 'Create Paperpal account clicked'),
  sle('Sign-up Intent', 'Filling Details_b', 'Email ID'),
  sle('Sign-up Intent', 'Filling Details_b', 'Submit Email ID'),
  sle('Sign-up Intent', 'Filling Details_b', 'Marketing_consent_given'),
  sle('Checkout', 'Payment_process', 'Marketing_consent_given'),
  sle('Result_page', 'Download_document', 'More_Recommendations_Label_Clicked'),
  sle('Data_Security', 'Top_CTA', 'Upload_Manuscript_Info_Button'),
  sle('Data_Security', 'Bottom_CTA', 'Why_Preflight_Data_Security_Details'),
  sle('SSO', 'Filling Details', 'Login_button_Clicked'),
  sle('SSO', 'Filling Details', 'Forgot_password_success'),
  sle('SSO', 'Filling Details', 'Forgot_password_link'),
  sle('SSO', 'Filling Details', 'Signup_link'),
  sle('SSO', 'Filling Details', 'Get_otp_cta'),
  sle('SSO', 'Filling Details', 'Forgot_password_login_cta'),
  sle('SSO', 'Filling Details', 'Forgot_password_try_again_link'),
  sle('SSO', 'Filling Details', 'Terms_accepted'),
  sle('SSO', 'OTP', 'Sign_in_with_OTP'),
  sle('Error', 'Filling Details', 'Email ID'),
  sle('Error', 'Filling Details', 'Terms_accepted'),
  sle('Error', 'Filling Details', 'Get started failed'),
  sle('Sign-up Intent', 'Filling Details', 'Sign up successful'),
  sle('Sign-up Intent', 'Filling Details', 'Log in'),
  sle('Sign-in Intent', 'Login_form', 'Log in'),
  sle('User_history_page', 'Delete_ducument', 'Delete_ducument'),
  sle('Header', 'Logout_button', 'logout'),
  sle('Header', 'Login_button', 'Log in'),
] as const;

type CustomData = { [key: string]: string | number };

type TDynamicLabelEvent<
  TEventCategory extends string,
  TEventAction extends string,
  TEventLabel extends string,
  TCustomData extends CustomData,
> = [TEventCategory, TEventAction, TEventLabel, TCustomData];

type DynamicLabelEvent = TDynamicLabelEvent<string, string, string, CustomData>;

export type TrackingEvent = StaticLabelEvent | DynamicLabelEvent;

type UserDetails = {
  name: string;
  ssoId: number;
  email: string;
};

type PageView = {
  productName: string;
  journalName: string;
  publisherName: string;
  flowType: string;
  anonymousOrSignedIn: string;
  pageURL: string;
  sourceURL: string;
  ip: string;
  pageTitle: string;
  utm_source: string | string[] | undefined;
  utm_medium: string | string[] | undefined;
  utm_campaign: string | string[] | undefined;
};

const TrackingContext = React.createContext<{
  trackEvent: (event: TrackingEvent) => void;
  specialEvents: {
    signIn: (userDetails: UserDetails) => void;
    pageView: (pageView: Partial<PageView>) => void;
  };
}>({
  trackEvent: () => {},
  specialEvents: {
    signIn: () => {},
    pageView: () => {},
  },
});

type Publisher = {
  slug: string;
};

const AUTH_TOKEN_KEY = 'paperpal-auth-token';

export function useEventTracking() {
  return React.useContext(TrackingContext);
}

export const TrackingContextProvider = ({
  pageProps,
  children,
}: React.PropsWithChildren<{
  pageProps: {
    journal?: { gatherAnalytics: boolean; slug: string; publisher: Publisher };
  };
}>) => {
  const router = useRouter();
  const [baseUrl, setBaseUrl] = React.useState('');
  const [flowType, setFlowType] = React.useState('other_journey');
  const [clientIP, setClientIP] = React.useState('');
  useEffect(() => {
    const host = window.location.host;
    const baseUrl = `https://${host}`;
    setBaseUrl(`${baseUrl}`);

    if (router.pathname.indexOf('submit') > -1) {
      setFlowType('submission_journey');
    }
  }, [router.pathname]);

  useEffect(() => {
    const fetchClientAddress = async () => {
      try {
        const clientAddress = await getCountryCodeAllInfo();
        if (clientAddress) {
          setClientIP(clientAddress?.ip ?? '');
        }
      } catch (error) {
        console.error('Failed to fetch client address:', error);
      }
    };

    fetchClientAddress();
  }, []);

  const environment = getEnvironment(process.env.NEXT_PUBLIC_ENV);

  const ref = React.useRef<string | null>(null);

  const { trackEvent, specialEvents } = useCommonTracking();
  //Track previous url
  useEffect(() => {
    const handleRouteChangeStart = () => {
      ref.current = router.asPath;
    };

    router.events?.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router, specialEvents]);

  if (debounce) {
    clearTimeout(debounce);
  }

  if (typeof window === 'object') {
    debounce = setTimeout(() => {
      const pathName = window?.location?.pathname;
      if (pathName?.includes('/partner')) {
        sessionStorage.setItem('previousUrl', pathName);
      }
    }, 0);
  }

  return (
    <TrackingContext.Provider
      value={{
        trackEvent: ([category, action, label, customDataSet]) => {
          if (environment === 'LOCAL' || environment === 'PREVIEW') {
            console.info(`Event: ${category} | ${action} | ${label}`);
          }
          //These common Custom properties will get added in all track event.
          //We dont ned to add anywhere else
          const customData = {
            ProductName: process.env.NEXT_PUBLIC_SSO_PRODUCT,
            JournalName: pageProps.journal?.slug ?? '',
            PublisherName: pageProps.journal?.publisher.slug ?? '',
            FlowType: flowType,
            AnonymousOrSignedIn: localStorage.getItem(AUTH_TOKEN_KEY)
              ? 'signed_in'
              : 'anonymous',
            PageURL: `${baseUrl}${router.asPath}`,
            SourceURL: ref.current ? `${baseUrl}${ref.current}` : '',
            Ip: clientIP,
            PageTitle: document.title,
            CtaLable: label,
            ...customDataSet,
          };

          trackEvent({ action, category, label, customData });
        },
        specialEvents,
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
};
